import { Expose, plainToClass, plainToInstance, Transform, Type } from 'class-transformer';
import { GroupEntity, RecurringDate } from '@core/entities/groups';
import { BaseEntity, PaginatedEntity } from '@core/entities/_extra';
import { UserEntity } from '@core/entities/user.entity';
import { ApprovedStatus, EventType, TaskType } from '@core/enums';
import { SafeUrl } from '@angular/platform-browser';
import { TaskCollaborator } from '@core/entities/task.entity';
import { InitializableEntity } from '@core/entities/_extra/intializable.entity';
import { WorkShiftEntity } from '@core/entities/workShift.entity';
import { BeautifyArrayOfNumbers } from '@core/utils';

export class EventsList extends PaginatedEntity {
  @Type(() => EventEntity)
  events: EventEntity[];
}

export class EventEntity extends BaseEntity<EventEntity> {
  type: EventType = EventType.ClubEvent;

  @Expose()
  @Transform((item) => encodeURIComponent(item.value), { toPlainOnly: true })
  @Transform((item) => decodeURIComponent(item.value), { toClassOnly: true })
  name = '';

  place = '';
  room: string;
  visibility = 0;

  @Expose({ name: 'participants' })
  participantsText = 'participants';

  @Expose()
  @Transform((item) => encodeURIComponent(item.value), { toPlainOnly: true })
  @Transform((item) => decodeURIComponent(item.value), { toClassOnly: true })
  title = '';

  tags: string;
  author = 0;
  audience: number;

  @Expose({ name: 'group_id' })
  groupId = 0;

  @Expose({ name: 'date_from' })
  dateFrom = '';

  @Expose({ name: 'date_to' })
  dateTo = '';

  @Expose({ name: 'coorganizers' })
  coOrganizers: any;

  @Expose({ name: 'invite_friends' })
  inviteFriends: any;

  @Expose()
  @Transform((item) => encodeURIComponent(item.value), { toPlainOnly: true })
  @Transform((item) => decodeURIComponent(item.value), { toClassOnly: true })
  description = '';

  @Expose({ name: 'show_guest_list' })
  showGuestList = false;

  chargeable = '0';

  @Expose({ name: 'price_per_participant' })
  pricePerParticipant = '0';

  @Expose({ name: 'start_time' })
  startTime = '';

  @Expose({ name: 'end_time' })
  endTime = '';

  @Expose({ name: 'date_repeat' })
  dateRepeat = '';

  @Expose()
  recurrence = '';

  @Expose({ name: 'recurring_dates' })
  @Transform(({ value }) => {
    try {
      return value ? plainToClass(RecurringDate, JSON.parse(value)) : null;
    } catch (error) {
      console.error('Failed to parse recurring dates:', error);
      return null;
    }
  })
  recurringDates?: RecurringDate[];

  @Expose({ name: 'allowed_persons' })
  allowedPersons: string;

  @Expose({ name: 'eventsTags' })
  @Type(() => EventTag)
  eventTags: EventTag[];

  @Expose({ name: 'eventUsers' })
  @Type(() => EventParticipant)
  participants?: EventParticipant[];

  // USED in updated record only
  @Transform((item) => BeautifyArrayOfNumbers(item.value))
  participant: number[];

  document: string;

  // Temp For UI only
  isMember: boolean;
  currentDate: string;
  imageUrl: string | SafeUrl = '';
  isWorkShift = false;
  isHidden = false;

  // for POST/PUT request
  @Expose({ name: 'event_tags' })
  eventTagsArray: string[] = [];

  isTask = false;

  @Type(() => EventTask)
  task: EventTask = new EventTask();

  @Type(() => EventTask)
  eventTask: EventTask;

  image: string;

  @Transform(
    ({ value }) => {
      try {
        return value ? plainToInstance(RecurringDate, JSON.parse(value)) : null;
      } catch (error) {
        console.error('Failed to parse recurring dates:', error);
        return null;
      }
    },
    { toClassOnly: true },
  )
  @Type(() => RecurringDate)
  eventDate: RecurringDate[] = [];

  // New property to hold user selected dates
  @Type(() => UserSelectedDates)
  selectedDates: UserSelectedDates[];

  @Expose({ name: 'eventLeaveUser' })
  @Type(() => UserSelectedDates)
  userLeaveDates: UserSelectedDates[];

  @Type(() => EventUser)
  users: EventUser[] = [];

  // In event details it is used to show the event creator
  @Type(() => UserEntity)
  user: UserEntity;

  // Work Shift Entity Association
  @Expose({ name: 'eventshift' })
  @Type(() => WorkShiftEntity)
  eventShifts: WorkShiftEntity[] = [];

  // Group for Event Details
  group: GroupEntity;

  /**
   * For UI sorting only
   */
  initialTime: string;
  /**
   * For UI sorting only
   */
  initialDate: string;
  /**
   * For UI sorting only
   */
  shiftsCount: number;

  constructor(values?: Partial<EventEntity>) {
    super(values);
    this.initEntity(values);
  }
}

export class EventParticipantsList extends PaginatedEntity {
  @Type(() => EventParticipant)
  members: EventParticipant[];
}

export class EventParticipant {
  id: number;

  @Expose({ name: 'user_id' })
  userId: number;

  @Expose({ name: 'event_id' })
  eventId: number;

  @Expose({ name: 'approved_status' })
  approvedStatus: ApprovedStatus;

  @Expose({ name: 'read_status' })
  readStatus: number;

  @Expose({ name: 'event_updated_status' })
  eventUpdatedStatus: number;

  @Expose({ name: 'users' })
  @Type(() => UserEntity)
  user?: UserEntity;
}

export class EventUser {
  @Expose({ name: 'user_id' })
  userId: number;

  @Expose({ name: 'approved_status' })
  approvedStatus: ApprovedStatus;
}

export class EventTag {
  @Expose({ name: 'event_id' })
  eventId: number;
  id: number;

  @Expose({ name: 'tag_id' })
  tagId: number;

  @Expose({ name: 'tag_name' })
  tagName: string;
}

export class EventTask extends InitializableEntity {
  id: number;
  title = '';
  description = '';

  @Expose({ name: 'organizer_id' })
  organizerId = 0;

  status = 0;

  @Expose({ name: 'group_id' })
  groupId = 0;

  groups: any = [];

  @Expose({ name: 'user_select' })
  @Type(() => EventTaskUser)
  userSelect: EventTaskUser[] = [];

  date = '';

  @Expose({ name: 'type_dropdown' })
  type: TaskType = TaskType.Individual;

  @Type(() => TaskCollaborator)
  taskCollaborators: TaskCollaborator[] = [];

  // For GET/PUT request
  @Expose({ name: 'collaborator' })
  @Type(() => TaskCollaborator)
  collaborators: TaskCollaborator[] = [];

  constructor(values?: Partial<EventTask>) {
    super();
    this.initEntity(values);
  }
}

export class EventTaskUser {
  @Expose({ name: 'user_id' })
  userId? = 0;

  id? = 0;

  @Expose({ name: 'user_name' })
  username = '';
}

// For User selected event dates

export class EventSelectedDates {
  @Expose({ name: 'start_date' })
  startDate = '';

  @Expose({ name: 'end_date' })
  endDate = '';
}

export class UserSelectedDates extends EventSelectedDates {
  id: number;

  @Expose({ name: 'user_id' })
  userId: number;

  @Expose({ name: 'event_id' })
  eventId: number;

  @Expose({ name: 'users' })
  @Type(() => UserEntity)
  user: Pick<UserEntity, 'id' | 'userName' | 'firstName' | 'lastName' | 'email' | 'memberId'>;
}

export class EventSelectedDatesDTO extends InitializableEntity {
  @Expose()
  @Type(() => EventSelectedDates)
  eventDates: EventSelectedDates[];

  constructor(values?: Partial<EventSelectedDatesDTO>) {
    super();
    this.initEntity(values);
  }
}

//  For user leaving event dates

export class UserEventLeaveDates {
  @Expose()
  id: number;

  @Expose({ name: 'start_date' })
  startDate = '';
}

export class UserEventLeaveDatesDTO extends InitializableEntity {
  @Expose({ name: 'leaveDate' })
  @Type(() => UserEventLeaveDates)
  leaveDates: UserEventLeaveDates[];

  constructor(values?: Partial<UserEventLeaveDatesDTO>) {
    super();
    this.initEntity(values);
  }
}
